import {
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
  GridCellParams,
  GridColDef,
  GridValidRowModel,
} from '@mui/x-data-grid-pro';
import { DataPointWithValue } from '@src/types/DataPoint';
import { PointConfigRef, ValueGenerationFunction } from '@src/types/Widget';
import { createFieldIdentifier } from '@src/utils/gridUtils';
import { getValueGenerationFunction } from '@utils/evaluateValueGenerationFunction';
import { DEFAULT_PRECISION, isValidNumber } from '@utils/index_ts';
import { compareAlphaNum } from '@utils/sortAlphaNum';
import NotesButton from '../Notes/NotesButton';
import Cell from './Cell';
import CommandButtonColumn from './CommandButtonColumn';
import CustomDetailPanelToggle from './CustomDetailPanelToggle';

export function createStaticColumns(
  alias: (text: string) => string = (text) => text,
): GridColDef[] {
  return [
    {
      field: 'id',
      headerName: alias('Device ID'),
      flex: 1,
      type: 'string',
      align: 'center',
      headerAlign: 'center',
      sortComparator: (v1, v2) => compareAlphaNum(String(v1), String(v2)),
    },
    {
      field: 'name',
      headerName: alias('Device'),
      flex: 1,
      type: 'string',
      align: 'center',
      headerAlign: 'center',
      sortComparator: (v1, v2) => compareAlphaNum(String(v1), String(v2)),
    },
  ];
}

export function createCommandColumn(
  openCmdModal: (deviceId: string) => void,
  disableBtn?: boolean,
  disabledByInterlock?: boolean,
  alias: (text: string) => string = (text) => text,
): GridColDef {
  return {
    field: 'cmdBtn',
    headerName: alias('CMD'),
    flex: 0.6,
    sortable: false,
    filterable: false,
    type: 'string',
    align: 'center',
    headerAlign: 'center',
    renderCell: (params) => (
      <CommandButtonColumn
        deviceId={params.row.id}
        type={params.row.type}
        openCmdModal={openCmdModal}
        disabled={disableBtn}
        disabledByInterlock={disabledByInterlock}
      />
    ),
  };
}

export function createNotesColumn(
  alias: (text: string) => string = (text) => text,
): GridColDef {
  return {
    field: 'notes',
    headerName: alias('Notes'),
    flex: 0.6,
    sortable: false,
    filterable: false,
    type: 'string',
    align: 'center',
    headerAlign: 'center',
    renderCell: (params) => (
      <NotesButton
        className='cell-with-cursor-pointer'
        deviceId={params.row.id}
        deviceName={params.row.name}
      />
    ),
  };
}

export function createCustomColumn(
  customColumn: (params: GridCellParams) => React.ReactNode,
): GridColDef {
  return {
    field: 'status',
    headerName: 'Status',
    flex: 1,
    type: 'string',
    align: 'center',
    headerAlign: 'center',
    sortable: false,
    renderCell: (params) => <>{customColumn(params)}</>,
  };
}

export function createLiveDataColumns(
  pointConfigRefs: PointConfigRef[],
): GridColDef[] {
  const visiblePointConfigRefs = pointConfigRefs.filter(
    (pointConfigRef) => !pointConfigRef.hidden,
  );

  return visiblePointConfigRefs.map((pc, index): GridColDef => {
    const func = getValueGenerationFunction(
      pc.valueGenerationFunction?.function,
    );
    const fieldIdentifier = createFieldIdentifier(pc);
    const fieldName =
      fieldIdentifier.fieldName || `missing_point_config_${index}`;

    return {
      field: fieldName,
      headerName: pc.displayName || pc.pointName || fieldName,
      flex: 1,
      type: 'string',
      align: 'center',
      cellClassName: 'fractal-datapoint-value-cell',
      headerAlign: 'center',
      ...(func
        ? {
            valueGetter: (
              value: DataPointWithValue,
              row: GridValidRowModel,
            ): DataPointWithValue =>
              ({
                ...value,
                value: func(
                  (
                    pc.valueGenerationFunction as ValueGenerationFunction
                  ).argumentArray.map((pointAlias) => row[pointAlias]?.value),
                )?.toFixed(
                  isValidNumber(pc.decimalPrecision)
                    ? pc.decimalPrecision
                    : DEFAULT_PRECISION,
                ),
                unitOfMeasurement: pc.unitOfMeasurement,
              }) as DataPointWithValue,
          }
        : {}),
      renderCell: (
        params: GridCellParams<GridValidRowModel, DataPointWithValue>,
      ) => (params.value ? <Cell dataPoint={params.value} /> : '-'),
    };
  });
}

export function createDetailPanelColumn(): GridColDef {
  return {
    ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
    renderCell: (params: GridCellParams) => (
      <CustomDetailPanelToggle id={params.id} value={params.value as boolean} />
    ),
  };
}

export function getColumns(
  pointConfigRefs: PointConfigRef[],
  enableCmdBtnColumn?: boolean,
  enableNotesColumn?: boolean,
  customColumn?: (params: GridCellParams) => React.ReactNode,
  disableBtn?: boolean,
  disabledByInterlock?: boolean,
  openCmdModal?: (deviceId: string) => void,
  alias: (text: string) => string = (text) => text,
): GridColDef[] {
  const staticCols = createStaticColumns(alias);
  const columns: GridColDef[] = [...staticCols];

  if (enableCmdBtnColumn && openCmdModal !== undefined) {
    columns.push(
      createCommandColumn(openCmdModal, disableBtn, disabledByInterlock, alias),
    );
  }

  if (enableNotesColumn) {
    columns.push(createNotesColumn(alias));
  }

  if (customColumn) {
    columns.push(createCustomColumn(customColumn));
  }

  const liveDataCols = createLiveDataColumns(pointConfigRefs);
  columns.push(...liveDataCols);
  columns.push(createDetailPanelColumn());

  return columns;
}
